import { v4 as uuid } from 'uuid';

import { IWebchatChatSession } from 'modules/webchat/domain/entities';
import { IWebchatChatSessionsRepository } from 'modules/webchat/domain/repositories';
import IStorageProvider from 'shared/domain/providers/IStorageProvider';

const isChatSession = (value: unknown): value is IWebchatChatSession => {
  if (typeof value !== 'object' || value === null) return false;

  const { identifier, sessionId } = value as Record<string, unknown>;

  return typeof identifier === 'string' && typeof sessionId === 'string';
};

/**
 * Sala do visitante do pré-chat, guardada só nesta sessão do navegador
 * (sessionStorage): quem não está logado não recupera conversas antigas em
 * outra aba ou depois de fechar o navegador. Só vale enquanto a identidade do
 * pré-chat existir, então a sala de outro visitante é descartada quando uma
 * nova é criada. O cliente logado não passa por aqui: a sala dele é derivada
 * no Vectis a partir da sessão dele.
 */
export default class SessionStorageWebchatChatSessionsRepository
  implements IWebchatChatSessionsRepository
{
  constructor(private storageProvider: IStorageProvider) {}

  public findOrCreate: IWebchatChatSessionsRepository['findOrCreate'] =
    identifier => {
      const current = this.read().find(
        session => session.identifier === identifier,
      );

      if (current) return current.sessionId;

      const session: IWebchatChatSession = { identifier, sessionId: uuid() };

      this.write([session]);

      return session.sessionId;
    };

  public forget: IWebchatChatSessionsRepository['forget'] = identifier => {
    const sessions = this.read();

    if (!sessions.some(session => session.identifier === identifier)) return;

    this.write(sessions.filter(session => session.identifier !== identifier));
  };

  private read(): IWebchatChatSession[] {
    try {
      const stored: unknown = this.storageProvider.get('WEBCHAT_CHAT_SESSIONS');

      return Array.isArray(stored) ? stored.filter(isChatSession) : [];
    } catch {
      return [];
    }
  }

  private write(sessions: IWebchatChatSession[]) {
    try {
      this.storageProvider.store('WEBCHAT_CHAT_SESSIONS', sessions);
    } catch {
      // Sem storage, a sala vale só enquanto a página estiver aberta.
    }
  }
}
